// Confirmation prompts, in one place.
//
// Every destructive action in this app used to call window.confirm(). That is
// unstyled browser chrome, it blocks the event loop, and it cannot show the
// thing being acted on with any emphasis -- "Are you sure you want to delete
// job #46?" reads identically whether the job finished a minute ago or is the
// only copy of a model someone spent a day on.
//
// The guardrail itself is unchanged: everything that asked before still asks,
// with the same wording. Only the presentation moves.

import { writable } from "svelte/store";

/** null when nothing is being asked. @type {import("svelte/store").Writable<object|null>} */
export const confirmRequest = writable(null);

/**
 * @param {object} options
 * @param {string} options.title
 * @param {string} [options.message]
 * @param {string} [options.confirmLabel]
 * @param {string} [options.cancelLabel]
 * @param {"primary"|"error"} [options.tone] - "error" for destructive actions
 * @returns {Promise<boolean>} resolves false on cancel, Esc, or backdrop click
 */
export function confirmAction(options) {
  return new Promise((resolve) => {
    confirmRequest.set({
      title: options.title,
      message: options.message ?? "",
      confirmLabel: options.confirmLabel ?? "Confirm",
      cancelLabel: options.cancelLabel ?? "Cancel",
      tone: options.tone ?? "primary",
      // Called by ConfirmDialog. Clearing the store here rather than in the
      // component means the promise cannot be left dangling if the dialog is
      // unmounted mid-flight.
      settle(result) {
        confirmRequest.set(null);
        resolve(result);
      },
    });
  });
}
